import React, { useState } from 'react';

const STEP_TABS = [
  { id: 'review_match', label: 'Review & Match' },
  { id: 'customer_shipping', label: 'Customer & Shipping' },
  { id: 'summary_submit', label: 'Summary & Submit' },
];

const ALL_CONDITION_FIELDS = ['order_type', 'shipping_method'];

const emptyCondition = () => ({ field: 'order_type', op: 'eq', value: 'order' });

const getCondition = (whenClause) => (whenClause?.all && whenClause.all[0]) || emptyCondition();

const buildWhenClause = (condition) => ({ all: [condition] });

// Editable table of a step's field definitions: label, visible/required (plain or
// conditional on another field's value), and — for select fields — the valid
// value list (e.g. Shipping Method options). Mutates the shared config object
// immutably via onChange(nextConfig).
const FieldEditor = ({ stepKey, fields, onChange }) => {
  const updateField = (index, patch) => {
    const nextFields = fields.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange(nextFields);
  };

  return (
    <table className="table table-sm align-middle">
      <thead>
        <tr>
          <th>Field</th>
          <th style={{ width: 220 }}>Visible</th>
          <th style={{ width: 260 }}>Required</th>
          <th>Valid Values</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field, index) => {
          const isConditionalVisible = Object.prototype.hasOwnProperty.call(field, 'visible_when');
          const isConditionalRequired = Object.prototype.hasOwnProperty.call(field, 'required_when');
          const visibleCondition = getCondition(field.visible_when);
          const requiredCondition = getCondition(field.required_when);

          return (
            <tr key={field.key}>
              <td>
                <input
                  className="form-control form-control-sm"
                  value={field.label}
                  onChange={(e) => updateField(index, { label: e.target.value })}
                />
                <div className="text-muted small">{field.key}</div>
              </td>
              <td>
                {isConditionalVisible ? (
                  <div className="d-flex align-items-center gap-1 small">
                    <span>If</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 110 }}
                      value={visibleCondition.field}
                      onChange={(e) => updateField(index, { visible_when: buildWhenClause({ ...visibleCondition, field: e.target.value }) })}
                    >
                      {ALL_CONDITION_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <span>=</span>
                    <input
                      className="form-control form-control-sm"
                      style={{ width: 90 }}
                      value={visibleCondition.value}
                      onChange={(e) => updateField(index, { visible_when: buildWhenClause({ ...visibleCondition, value: e.target.value }) })}
                    />
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-danger"
                      onClick={() => {
                        const next = { ...field };
                        delete next.visible_when;
                        next.visible = true;
                        onChange(fields.map((f, i) => (i === index ? next : f)));
                      }}
                    >
                      clear
                    </button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={field.visible !== false}
                        onChange={(e) => updateField(index, { visible: e.target.checked })}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => updateField(index, { visible_when: buildWhenClause(emptyCondition()) })}
                    >
                      Conditional
                    </button>
                  </div>
                )}
              </td>
              <td>
                {isConditionalRequired ? (
                  <div className="d-flex align-items-center gap-1 small">
                    <span>If</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 110 }}
                      value={requiredCondition.field}
                      onChange={(e) => updateField(index, { required_when: buildWhenClause({ ...requiredCondition, field: e.target.value }) })}
                    >
                      {ALL_CONDITION_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <span>=</span>
                    <input
                      className="form-control form-control-sm"
                      style={{ width: 90 }}
                      value={requiredCondition.value}
                      onChange={(e) => updateField(index, { required_when: buildWhenClause({ ...requiredCondition, value: e.target.value }) })}
                    />
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-danger"
                      onClick={() => {
                        const next = { ...field };
                        delete next.required_when;
                        next.required = false;
                        onChange(fields.map((f, i) => (i === index ? next : f)));
                      }}
                    >
                      clear
                    </button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(field.required)}
                        onChange={(e) => updateField(index, { required: e.target.checked })}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => updateField(index, { required_when: buildWhenClause(emptyCondition()) })}
                    >
                      Conditional
                    </button>
                  </div>
                )}
              </td>
              <td>
                {field.type === 'select' ? (
                  <input
                    className="form-control form-control-sm"
                    value={(field.options || []).join(', ')}
                    onChange={(e) => updateField(index, {
                      options: e.target.value.split(',').map((v) => v.trim()).filter(Boolean),
                    })}
                    placeholder="Comma-separated values"
                  />
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const SummarySubmitEditor = ({ step, allFieldKeys, onChange }) => {
  const toggleKey = (listKey, fieldKey) => {
    const current = new Set(step[listKey] || []);
    if (current.has(fieldKey)) current.delete(fieldKey); else current.add(fieldKey);
    onChange({ ...step, [listKey]: Array.from(current) });
  };
  return (
    <div className="row g-4">
      <div className="col-12 col-md-6">
        <h6>Required to Save as Quote</h6>
        {allFieldKeys.map((key) => (
          <div className="form-check" key={`quote-${key}`}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`quote-${key}`}
              checked={(step.quote_required_fields || []).includes(key)}
              onChange={() => toggleKey('quote_required_fields', key)}
            />
            <label className="form-check-label" htmlFor={`quote-${key}`}>{key}</label>
          </div>
        ))}
      </div>
      <div className="col-12 col-md-6">
        <h6>Required to Submit as Order</h6>
        {allFieldKeys.map((key) => (
          <div className="form-check" key={`order-${key}`}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`order-${key}`}
              checked={(step.order_required_fields || []).includes(key)}
              onChange={() => toggleKey('order_required_fields', key)}
            />
            <label className="form-check-label" htmlFor={`order-${key}`}>{key}</label>
          </div>
        ))}
      </div>
    </div>
  );
};

const CheckoutFlowAdminSection = ({ config, onChange }) => {
  const [activeStepTab, setActiveStepTab] = useState('review_match');
  const steps = config?.steps || {};

  const updateStepFields = (stepKey, nextFields) => {
    onChange({
      ...config,
      steps: { ...steps, [stepKey]: { ...steps[stepKey], fields: nextFields } },
    });
  };

  const updateSummarySubmit = (nextStep) => {
    onChange({ ...config, steps: { ...steps, summary_submit: nextStep } });
  };

  const allFieldKeys = [
    ...(steps.customer_shipping?.fields || []).map((f) => f.key),
  ];

  return (
    <div>
      <ul className="nav nav-pills mb-3">
        {STEP_TABS.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              type="button"
              className={`nav-link ${activeStepTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveStepTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {activeStepTab === 'review_match' && (
        <FieldEditor
          stepKey="review_match"
          fields={steps.review_match?.fields || []}
          onChange={(nextFields) => updateStepFields('review_match', nextFields)}
        />
      )}
      {activeStepTab === 'customer_shipping' && (
        <FieldEditor
          stepKey="customer_shipping"
          fields={steps.customer_shipping?.fields || []}
          onChange={(nextFields) => updateStepFields('customer_shipping', nextFields)}
        />
      )}
      {activeStepTab === 'summary_submit' && (
        <SummarySubmitEditor
          step={steps.summary_submit || {}}
          allFieldKeys={allFieldKeys}
          onChange={updateSummarySubmit}
        />
      )}
    </div>
  );
};

export default CheckoutFlowAdminSection;
